/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { ProfileFilterHeader } from './components/ProfileFilterHeader';
import { ProfileCharts, ProfileChartsHandle } from './components/ProfileCharts';
import { ProfileDetailTable } from './components/ProfileDetailTable';
import { ProfileCustomFilter, CustomFilterValues } from './components/ProfileCustomFilter';
import { HEALTH_STATUS_OPTIONS } from '@/constants/health-status.constant';
import { ALL_PROVINCE_OPTIONS, AGE_GROUPS_OPTIONS } from '../constants';
import {
  getStatisticsProfiles,
  exportStatisticsProfilesExcel,
  ChartDataItem,
  StackedChartData,
  ProfileDetailItem,
  ViewByType,
} from '../services/statistics.service';
import { StatisticsAccessPage } from '../access';

export const StatisticsProfilesPage: React.FC = () => {
  const [mainTab, setMainTab] = useState<'profile' | 'access'>('profile');
  const [target, setTarget] = useState<'user' | 'relative' | 'all'>('user');
  const [viewBy, setViewBy] = useState<ViewByType>('age');
  const [subFilter, setSubFilter] = useState<string | string[]>('all');
  const [subFilter2, setSubFilter2] = useState<string | string[]>('all');
  const [period, setPeriod] = useState<'today' | 'this_month' | 'this_year' | 'all' | 'custom'>('all');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [fromDate, toDate] = dateRange;
  const requestVersion = useRef(0);
  const [customFilters, setCustomFilters] = useState<CustomFilterValues>({});

  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [stackedChartData, setStackedChartData] = useState<StackedChartData | undefined>(undefined);
  const [totalProfiles, setTotalProfiles] = useState<number>(0);
  
  const chartsRef = useRef<ProfileChartsHandle>(null);
  
  const [detailList, setDetailList] = useState<ProfileDetailItem[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const fetchData = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const offset = (currentPage - 1) * pageSize;
      const res = await getStatisticsProfiles({
        target,
        view_by: viewBy,
        sub_filter: Array.isArray(subFilter) ? subFilter.join(',') : subFilter,
        sub_filter_2: Array.isArray(subFilter2) ? subFilter2.join(',') : subFilter2,
        period,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        profile_types: customFilters.profileTypes?.join(','),
        search_name: customFilters.searchName,
        search_phone: customFilters.searchPhone,
        search_email: customFilters.searchEmail,
        search_user_id: customFilters.searchUserId,
        genders: customFilters.genders?.join(','),
        provinces: customFilters.provinces?.join(','),
        age_groups: customFilters.ageGroups?.join(','),
        dob_from: customFilters.dobFrom,
        dob_to: customFilters.dobTo,
        created_from: customFilters.createdFrom,
        created_to: customFilters.createdTo,
        offset,
        limit: pageSize,
      });

      if (version !== requestVersion.current) return;
      if (res && res.summary) {
        setChartData(res.summary.chartData || []);
        setStackedChartData(res.summary.stackedChartData);
        setTotalProfiles(res.summary.totalProfiles || 0);
      }
      if (res && res.detail) {
        setDetailList(res.detail.results || []);
        setTotalRows(res.detail.paging?.totalRows || 0);
      }
    } catch (err: any) {
      if (version !== requestVersion.current) return;
      console.error('Fetch statistics error:', err);
      message.error('Không thể tải dữ liệu thống kê');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [target, viewBy, subFilter, subFilter2, period, fromDate, toDate, customFilters, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
    return () => { requestVersion.current += 1; };
  }, [fetchData]);

  const handlePeriodChange = (val: 'today' | 'this_month' | 'this_year' | 'all' | 'custom') => {
    if (val === period && !fromDate && !toDate) {
      fetchData();
      return;
    }
    requestVersion.current += 1;
    setPeriod(val);
    setCurrentPage(1);
    if (val !== 'custom') {
      setDateRange([null, null]);
    }
  };

  const handleDateRangeChange = (range: [string | null, string | null]) => {
    const [from, to] = range;
    if (from && to && from > to) {
      message.error('Từ ngày phải nhỏ hơn hoặc bằng Đến ngày.');
      setDateRange([...dateRange]);
      return;
    }
    if (from === fromDate && to === toDate && period === (from || to ? 'custom' : 'all')) return;
    requestVersion.current += 1;
    setDateRange([from, to]);
    setPeriod(from || to ? 'custom' : 'all');
    setCurrentPage(1);
  };

  const handleViewByChange = (val: ViewByType) => {
    setViewBy(val);
    setPeriod('all');
    setDateRange([null, null]);
    setSubFilter('all');
    setSubFilter2('all');
    setCurrentPage(1);
  };

  const handleSubFilterChange = (val: string | string[]) => {
    setSubFilter(val);
    setCurrentPage(1);
  };

  const handleSubFilter2Change = (val: string | string[]) => {
    setSubFilter2(val);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      // Capture chart image snapshot if not in custom list mode
      let chartImageBase64: string | undefined;
      if (viewBy !== 'custom') {
        chartImageBase64 = chartsRef.current?.getChartImage() ?? undefined;
      }

      // Target display
      const targetLabel = target === 'all' ? 'Hồ sơ Tất cả' : (target === 'relative' ? 'Hồ sơ người thân' : 'Hồ sơ người dùng');
      const targetSubject = target === 'relative' ? 'người thân' : (target === 'all' ? 'người dùng và người thân' : 'người dùng');

      // View by display
      let viewByLabel = 'Độ tuổi';
      switch (viewBy) {
        case 'age': viewByLabel = 'Độ tuổi'; break;
        case 'gender': viewByLabel = 'Giới tính'; break;
        case 'province': viewByLabel = 'Tỉnh thành'; break;
        case 'health_status': viewByLabel = 'Tình trạng sức khỏe'; break;
        case 'age_gender': viewByLabel = 'Độ tuổi và giới tính'; break;
        case 'age_province': viewByLabel = 'Độ tuổi và tỉnh thành'; break;
        case 'age_health': viewByLabel = 'Độ tuổi và tình trạng sức khoẻ'; break;
        case 'health_gender': viewByLabel = 'Tình trạng sức khỏe và giới tính'; break;
        case 'province_gender': viewByLabel = 'Tỉnh thành và giới tính'; break;
        case 'province_health': viewByLabel = 'Tỉnh thành và tình trạng sức khỏe'; break;
        case 'custom': viewByLabel = 'Danh sách tuỳ chỉnh'; break;
      }

      // Chart Title (matching FE)
      let chartTitle = 'Thống kê';
      switch (viewBy) {
        case 'age': chartTitle = `Độ tuổi của ${targetSubject}`; break;
        case 'gender': chartTitle = `Giới tính của ${targetSubject}`; break;
        case 'province': chartTitle = `Tỉnh thành của ${targetSubject}`; break;
        case 'health_status': chartTitle = `Tình trạng sức khỏe của ${targetSubject}`; break;
        case 'age_gender': chartTitle = `Độ tuổi và giới tính của ${targetSubject}`; break;
        case 'age_province': chartTitle = `Độ tuổi và tỉnh thành của ${targetSubject}`; break;
        case 'age_health': chartTitle = `Độ tuổi và tình trạng sức khỏe của ${targetSubject}`; break;
        case 'health_gender': chartTitle = `Tình trạng sức khỏe và giới tính của ${targetSubject}`; break;
        case 'province_gender': chartTitle = `Tỉnh thành và giới tính của ${targetSubject}`; break;
        case 'province_health': chartTitle = `Tỉnh thành và tình trạng sức khỏe của ${targetSubject}`; break;
        case 'custom': chartTitle = 'Danh sách tuỳ chỉnh'; break;
        default: chartTitle = `Thống kê ${targetSubject}`; break;
      }

      // Filter labels & values
      const GENDER_OPTIONS_LOCAL = [
        { value: 'MALE', label: 'Nam' },
        { value: 'FEMALE', label: 'Nữ' },
        { value: 'NO_INFO', label: 'Không có thông tin' },
      ];
      const formatFilterVal = (val: string | string[], opts: Array<{ value: string; label: string }>) => {
        if (!val || val === 'all') return '-';
        const arr = Array.isArray(val) ? val : [val];
        if (arr.length === 0 || arr.includes('all')) return '-';
        return arr.map(v => opts.find(o => o.value === v)?.label || v).join(', ');
      };

      const filters: Array<{ label: string; value: string }> = [];
      if (viewBy === 'custom') {
        chartTitle = 'Thông tin bộ lọc tìm kiếm tuỳ chỉnh';
        if (customFilters.searchName) {
          filters.push({ label: 'Họ và tên:', value: customFilters.searchName });
        }
        if (customFilters.searchPhone) {
          filters.push({ label: 'Số điện thoại:', value: customFilters.searchPhone });
        }
        if (customFilters.searchEmail) {
          filters.push({ label: 'Email:', value: customFilters.searchEmail });
        }
        if (customFilters.searchUserId) {
          filters.push({ label: 'User ID:', value: customFilters.searchUserId });
        }
        if (customFilters.dobFrom || customFilters.dobTo) {
          const from = customFilters.dobFrom ? dayjs(customFilters.dobFrom).format('DD/MM/YYYY') : '...';
          const to = customFilters.dobTo ? dayjs(customFilters.dobTo).format('DD/MM/YYYY') : '...';
          filters.push({ label: 'Ngày sinh:', value: `${from} - ${to}` });
        }
        if (customFilters.genders && customFilters.genders.length > 0) {
          const genderStr = customFilters.genders
            .map(g => GENDER_OPTIONS_LOCAL.find(o => o.value === g)?.label || g)
            .join(', ');
          filters.push({ label: 'Giới tính:', value: genderStr });
        }
        if (customFilters.provinces && customFilters.provinces.length > 0) {
          filters.push({ label: 'Tỉnh thành:', value: customFilters.provinces.join(', ') });
        }
        if (customFilters.ageGroups && customFilters.ageGroups.length > 0) {
          const ageStr = customFilters.ageGroups
            .map(a => AGE_GROUPS_OPTIONS.find(o => o.value === a)?.label || a)
            .join(', ');
          filters.push({ label: 'Độ tuổi:', value: ageStr });
        }
        if (customFilters.createdFrom || customFilters.createdTo) {
          const from = customFilters.createdFrom ? dayjs(customFilters.createdFrom).format('DD/MM/YYYY') : '...';
          const to = customFilters.createdTo ? dayjs(customFilters.createdTo).format('DD/MM/YYYY') : '...';
          filters.push({ label: 'Thời gian tạo hồ sơ:', value: `${from} - ${to}` });
        }
        if (target === 'all' && customFilters.profileTypes && customFilters.profileTypes.length > 0) {
          const typeStr = customFilters.profileTypes
            .map(t => t === 'user' ? 'Hồ sơ người dùng' : 'Hồ sơ người thân')
            .join(', ');
          filters.push({ label: 'Loại hồ sơ:', value: typeStr });
        }
      } else {
        if (['age', 'age_gender', 'age_province', 'age_health'].includes(viewBy)) {
          filters.push({ label: 'Độ tuổi:', value: formatFilterVal(subFilter, AGE_GROUPS_OPTIONS) });
        } else if (['health_status', 'health_gender'].includes(viewBy)) {
          filters.push({ label: 'Tình trạng sức khoẻ:', value: formatFilterVal(subFilter, HEALTH_STATUS_OPTIONS) });
        } else if (['province', 'province_gender', 'province_health'].includes(viewBy)) {
          filters.push({ label: 'Tỉnh thành:', value: formatFilterVal(subFilter, ALL_PROVINCE_OPTIONS) });
        } else if (viewBy === 'gender') {
          filters.push({ label: 'Giới tính:', value: formatFilterVal(subFilter, GENDER_OPTIONS_LOCAL) });
        }

        if (['age_gender', 'health_gender', 'province_gender'].includes(viewBy)) {
          filters.push({ label: 'Giới tính:', value: formatFilterVal(subFilter2, GENDER_OPTIONS_LOCAL) });
        } else if (viewBy === 'age_province') {
          filters.push({ label: 'Tỉnh thành:', value: formatFilterVal(subFilter2, ALL_PROVINCE_OPTIONS) });
        } else if (['age_health', 'province_health'].includes(viewBy)) {
          filters.push({ label: 'Tình trạng sức khoẻ:', value: formatFilterVal(subFilter2, HEALTH_STATUS_OPTIONS) });
        }
      }

      // Time range label
      let timeRangeLabel = 'Tất cả';
      if (period === 'custom' && dateRange[0] && dateRange[1]) {
        timeRangeLabel = `${dayjs(dateRange[0]).format('DD/MM/YYYY')} - ${dayjs(dateRange[1]).format('DD/MM/YYYY')}`;
      } else if (period === 'custom' && dateRange[0]) {
        timeRangeLabel = `Từ ${dayjs(dateRange[0]).format('DD/MM/YYYY')}`;
      } else if (period === 'custom' && dateRange[1]) {
        timeRangeLabel = `Đến ${dayjs(dateRange[1]).format('DD/MM/YYYY')}`;
      } else if (period === 'today') {
        timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
      } else if (period === 'this_month') {
        timeRangeLabel = `${dayjs().startOf('month').format('DD/MM/YYYY')} - ${dayjs().endOf('month').format('DD/MM/YYYY')}`;
      } else if (period === 'this_year') {
        timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
      }

      await exportStatisticsProfilesExcel(
        {
          target,
          view_by: viewBy,
          sub_filter: Array.isArray(subFilter) ? subFilter.join(',') : subFilter,
          sub_filter_2: Array.isArray(subFilter2) ? subFilter2.join(',') : subFilter2,
          period,
          from_date: dateRange[0] || undefined,
          to_date: dateRange[1] || undefined,
          profile_types: customFilters.profileTypes?.join(','),
          search_name: customFilters.searchName,
          search_phone: customFilters.searchPhone,
          search_email: customFilters.searchEmail,
          search_user_id: customFilters.searchUserId,
          genders: customFilters.genders?.join(','),
          provinces: customFilters.provinces?.join(','),
          age_groups: customFilters.ageGroups?.join(','),
          dob_from: customFilters.dobFrom,
          dob_to: customFilters.dobTo,
          created_from: customFilters.createdFrom,
          created_to: customFilters.createdTo,
        },
        chartImageBase64,
        {
          target_label: targetLabel,
          view_by_label: viewByLabel,
          chart_title: chartTitle,
          filters,
          time_range_label: timeRangeLabel,
        }
      );
      message.success('Xuất báo cáo Excel thành công!');
    } catch (err: any) {
      // HTTP 422 = Data Cap exceeded — show warning instead of error
      if (err?.message?.includes('50.000') || err?.message?.includes('vượt quá')) {
        message.warning(err.message);
      } else {
        message.error(err?.message || 'Không thể xuất báo cáo Excel. Vui lòng thử lại sau.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  if (mainTab === 'access') {
    return (
      <StatisticsAccessPage
        activeMainTab={mainTab}
        onMainTabChange={setMainTab}
      />
    );
  }

  return (
    <div css={pageContainerStyle}>
      <ProfileFilterHeader
        activeMainTab={mainTab}
        onMainTabChange={setMainTab}
        target={target}
        onTargetChange={(t) => { setTarget(t); setSubFilter('all'); setSubFilter2('all'); setCurrentPage(1); }}
        viewBy={viewBy}
        onViewByChange={handleViewByChange}
        onExportExcel={handleExportExcel}
        exportLoading={exportLoading}
        exportDisabled={loading}
      />

      {viewBy === 'custom' ? (
        <ProfileCustomFilter
          target={target}
          loading={loading}
          onSearch={(filters) => {
            setCustomFilters(filters);
            setCurrentPage(1);
          }}
          onReset={() => {
            setCustomFilters({});
            setCurrentPage(1);
          }}
        />
      ) : (
        <ProfileCharts
          ref={chartsRef}
          loading={loading}
          viewBy={viewBy}
          subFilter={subFilter}
          onSubFilterChange={handleSubFilterChange}
          subFilter2={subFilter2}
          onSubFilter2Change={handleSubFilter2Change}
          period={period}
          onPeriodChange={handlePeriodChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          chartData={chartData}
          stackedChartData={stackedChartData}
          totalProfiles={totalProfiles}
          target={target}
        />
      )}

      <ProfileDetailTable
        target={target}
        data={detailList}
        loading={loading}
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default StatisticsProfilesPage;

const pageContainerStyle = css`
  background: var(--gray-soft-color);
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;
